import Button from '@berlitz/button'
import { Cancelled, Flip, Warning } from '@berlitz/icons'
import MyBerlitzLesson from '@classes/Lesson'
import CounsellingRegButton from '@components/CounsellingRegButton'
import JoinLessonButton from '@components/JoinLessonButton'
import LessonRegButton, { TextLabel } from '@components/LessonRegButton'
import { LessonInfo, LessonStatus, LessonTypeIcon, ListCard } from '@components/ListCard'
import { Status, StyledIcon } from '@components/ListCard/style'
import { GroupProfileModal, SingleProfileModal } from '@components/Modal'
import SelfTechCheck from '@components/SelfTechCheck'
import { Timeline, TimelineCardBox, TimelineCardContent, TimelineItem } from '@components/Timeline'
import ViewMaterial from '@components/ViewMaterial'
import ViewMaterialError from '@components/ViewMaterial/Error'
import ViewMaterialLoader from '@components/ViewMaterial/Loader'
import QueryMaterialData from '@components/ViewMaterial/QueryMaterialData'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { SessionInfoList } from '@interfaces/LessonGroups'
import { GroupClassListItem } from '@interfaces/MySchedule'
import { ProfileProps } from '@interfaces/UserProfile'
import EventJoinOnlineBtn from '@layouts/Events/EventJoinOnlineBtn'
import ModalCancelSeat from '@layouts/Events/ModalCancelSeat'
import {
  AtRiskContainer,
  EventJoinOnlineBtnWrapper,
  EventOpenMaterialBtnWrapper,
} from '@layouts/MyLessons/Lessons/style'
import { GET_VIRTUAL_CLASSROOM_INPUT } from '@queries/index'
import { DATE_FORMAT } from '@utils/constants'
import { getAtRiskData } from '@utils/helpers/instructor-scheduling'
import { displayLessonRegBtn, isFlexProgram, isOrientation, isBoD1 } from '@utils/helpers/sessions'
import { nonLessons } from '@utils/lesson-type'
import every from 'lodash/every'
import get from 'lodash/get'
import head from 'lodash/head'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import * as Moment from 'moment'
import { extendMoment } from 'moment-range'
import React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import MINOteModal from '../MINoteModal'
import { GroupModalData, SingleModalData, TimelineSectionProp } from './interface'
import LessonGroupName from './LessonGroupName'
import {
  ActionBox,
  Box,
  CompletedTick,
  IconStatus,
  Indicators,
  LessonInfoBox,
  LessonInfoFooter,
  MIContainer,
  NoteCol,
  PendingButtons,
  SelfTechCheckWrapper,
  EventButtonsWrapper,
} from './style'
import PortalTimeFormat from '@components/PortalTimeFormat'
import { IBerlitzEventAttendeeInfo } from '@interfaces/Events'
import EventOpenMaterialBtn from '@layouts/Events/EventOpenMaterialBtn'
import Spacer from '@berlitz/spacer'
import { EVENT_ATTENDEE_ROLE_HOST } from '@layouts/Events/constants'
import EventOpenMaterialModal from '@layouts/Events/EventOpenMaterialModal'

const moment = extendMoment(Moment)

class TimelineSection extends React.Component<
  TimelineSectionProp & InjectedIntlProps & WithUserTimeProps & ProfileProps & WithLessonUtilsProp
> {
  state = {
    groupModalOpen: false as boolean,
    groupModalData: {} as GroupModalData,
    singleModalOpen: false as boolean,
    singleModalData: {} as SingleModalData,
    openCancelModal: false as boolean,
    selectedEvent: {} as IBerlitzEventAttendeeInfo,
    miNote: '',
    getVirtualClassroomInput: null as GET_VIRTUAL_CLASSROOM_INPUT | null,
    getVirtualClassroom: false,
    timeFormat: DATE_FORMAT.HM,
    modalEventMaterialError: false,
  }

  toggleGroupModal = () =>
    this.setState({
      groupModalOpen: !this.state.groupModalOpen,
    })

  toggleSingleModal = () =>
    this.setState({
      singleModalOpen: !this.state.singleModalOpen,
    })

  toggleOpenCancelModal = (open?: boolean, event?: IBerlitzEventAttendeeInfo) =>
    this.setState({
      openCancelModal: open,
      selectedEvent: event || this.state.selectedEvent,
    })

  setMINote = (miNote: string) => this.setState({ miNote })

  checkScheduleCrossover = (sched) => {
    const startTime = moment.utc(sched.StartTime).tz(this.props.profile?.Timezone)
    const endTime = moment.utc(sched.EndTime).tz(this.props.profile?.Timezone)
    const isStartSameAsCurrent = startTime.isSame(this.props.currentDate, 'day')
    if (startTime.isSame(endTime, 'day')) {
      return 0
    }

    if (isStartSameAsCurrent) {
      return -1
    }

    return 1
  }

  getTimelineSpaces = (sched) => {
    const startTime = moment.utc(sched.StartTime).tz(this.props.profile?.Timezone)
    const endTime = moment.utc(sched.EndTime).tz(this.props.profile?.Timezone)
    let diff = Math.ceil(endTime.diff(startTime, 'm') / 15)

    const crossOver = this.checkScheduleCrossover(sched)

    if (crossOver !== 0) {
      if (crossOver < 0) {
        diff = Math.ceil(endTime.clone().startOf('day').diff(startTime, 'm') / 15)
      } else if (crossOver > 0) {
        diff = Math.ceil(endTime.diff(endTime.clone().startOf('day'), 'm') / 15)
      }
    }

    return diff
  }

  getTimelineColor = (TimeType?: string | null) =>
    get(
      {
        Flex: 'warning',
        Base: 'success',
      },
      TimeType || ''
    )

  getBlockColor = (time: string, spaces: number) => (this.isPastTimeSlot(time, spaces) ? 'disabled' : 'info')

  getCardColor = (time: string, spaces: number, Status?: string | null, isAbsent?: boolean) => {
    const color = get(
      {
        completed: 'disabled',
        confirmed: 'success',
        cancelled: 'error',
        pending: 'pending',
        rejected: 'error',
      },
      (Status || '').toLowerCase()
    )

    if (['completed', 'confirmed'].includes((Status || '').toLowerCase()) && isAbsent) {
      return 'error'
    }

    return color !== 'error' && this.isPastTimeSlot(time, spaces) ? 'disabled' : color
  }

  getLessonTime = (data: GroupClassListItem) =>
    `${this.props.toUserDate(data?.StartTime, undefined, DATE_FORMAT.HM)}-${this.props.toUserDate(
      data?.EndTime,
      undefined,
      DATE_FORMAT.HM
    )}`

  getQueryParams = (data: GroupClassListItem, isCounselling?: boolean) => {
    const { instructorId } = this.props

    const getDate = (key: string) => moment.utc(`${get(data, key)}`, DATE_FORMAT.DT).format()

    if (isCounselling) {
      return `id=${instructorId}&sd=${getDate('StartTime')}&ed=${getDate('EndTime')}&at=${get(
        data,
        'SessionMetaData.Index'
      )}&back=instructors/scheduling`
    }

    return `id=${instructorId}&sd=${getDate('SessionMetaData.StartTime')}&ed=${getDate(
      'SessionMetaData.EndTime'
    )}&at=${get(data, 'SessionMetaData.Index')}&back=instructors/scheduling&session=${data.SessionId}`
  }

  isDateToday = () => moment(this.props.currentDate).isSame(this.props.getUserDate(), 'day')

  isInMiddleOfLesson = (time: string, spaces: number) => {
    const { currentDate, getUserDate, getDateWithUserTimezone } = this.props
    const d = currentDate.format(DATE_FORMAT.BASIC)
    const st = moment(time, DATE_FORMAT.HM).format('HH:mm:ss')
    const et = moment(time, DATE_FORMAT.HM).format('HH:mm:ss')

    return getUserDate().isBetween(
      getDateWithUserTimezone(`${d} ${st}`),
      getDateWithUserTimezone(`${d} ${et}`).add(15 * spaces, 'minutes')
    )
  }

  isPastTimeSlot = (time: string, spaces: number) => {
    const { currentDate, getUserDate, getDateWithUserTimezone } = this.props
    const d = currentDate.format(DATE_FORMAT.BASIC)
    const t = moment(time, DATE_FORMAT.HM).format('HH:mm:ss')

    return (
      (getUserDate().isAfter(getDateWithUserTimezone(`${d} ${t}`).add(15 * spaces, 'minutes')) &&
        getUserDate().isSame(currentDate, 'day')) ||
      getUserDate().isAfter(currentDate, 'day')
    )
  }

  handleGroupClick = (sched: GroupClassListItem) => {
    if (includes((sched.ProgramType || '').toLowerCase(), 'group')) {
      this.setState({
        groupModalData: {
          groupId: sched.StudentGroupId,
          groupName: sched.GroupName,
          programId: sched.ProgramId,
          language: sched.Language,
          students: sched.SessionInfoList,
        },
      })
      this.toggleGroupModal()
    }

    if (sched.ProgramType === 'Individual') {
      this.setState({
        singleModalData: {
          StudentId: sched.StudentGroupId,
          StudentName: sched.GroupName,
          ProgramId: sched.ProgramId,
          Language: get(sched, 'Language', 'English'),
        },
      })
      this.toggleSingleModal()
    }
  }

  getTimelineRange = () => {
    const { GroupClassList, toUserDate, loading, showAll, currentDate } = this.props
    const defaultRange = {
      startTime: '00:00',
      endTime: '23:59',
    }

    if (loading) {
      return defaultRange
    }

    const today = currentDate.clone()

    const mins = orderBy(GroupClassList, ['StartTime']).map(({ StartTime }) =>
      moment(toUserDate(StartTime, DATE_FORMAT.DT, DATE_FORMAT.DT), DATE_FORMAT.DT)
    )
    const maxes = orderBy(GroupClassList, ['EndTime']).map(({ EndTime }) =>
      moment(toUserDate(EndTime, DATE_FORMAT.DT, DATE_FORMAT.DT), DATE_FORMAT.DT)
    )

    let maxStartTime = moment.min(mins)
    maxStartTime = maxStartTime.isSame(today, 'day') ? maxStartTime : today.clone().startOf('day')

    let maxEndTime = moment.max(maxes)
    maxEndTime = maxEndTime.isSame(today, 'day') ? maxEndTime : today.clone().endOf('day')

    if (showAll && !loading) {
      const hasData = GroupClassList.length > 0
      const hasMinRange =
        hasData && moment(defaultRange.startTime, DATE_FORMAT.HM).format('HHmm') >= maxStartTime.format('HHmm')
      const hasMaxRange =
        hasData && maxEndTime.format('HHmm') >= moment(defaultRange.endTime, DATE_FORMAT.HM).format('HHmm')
      return {
        startTime: hasMinRange ? maxStartTime.format(DATE_FORMAT.HM) : defaultRange.startTime,
        endTime: hasMaxRange ? maxEndTime.format(DATE_FORMAT.HM) : defaultRange.endTime,
      }
    }

    const minStart = moment.min(mins)
    const maxEnd = moment.max(maxes)

    const currentBasicDate = currentDate.format(DATE_FORMAT.BASIC)
    const start =
      currentBasicDate === minStart.format(DATE_FORMAT.BASIC) ? minStart : currentDate.clone().startOf('day')
    const end = currentBasicDate === maxEnd.format(DATE_FORMAT.BASIC) ? maxEnd : currentDate.clone().endOf('day')

    return {
      startTime: start.format(DATE_FORMAT.HM),
      endTime: (end.isBefore(end.clone().endOf('day')) ? end : end).format(DATE_FORMAT.HM),
    }
  }

  getDateTime = (dateTime: string, displayFormat: string) =>
    this.props.toUserDate(dateTime, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]', displayFormat)

  render() {
    const {
      groupModalOpen,
      groupModalData,
      singleModalOpen,
      singleModalData,
      openCancelModal,
      selectedEvent,
      miNote,
      modalEventMaterialError,
    } = this.state
    const {
      loading,
      GroupClassList,
      currentDate,
      onAccept = () => {},
      onDecline = () => {},
      withPendingButtons,
      isCutOff,
      getUserDate,
    } = this.props
    const { startTime, endTime } = this.getTimelineRange()

    return (
      <Box>
        <Timeline
          loading={loading}
          startTime={startTime}
          endTime={endTime}
          currentDate={currentDate}
          dataTimeKey="StartTime"
          dataTimeKeyFormat={DATE_FORMAT.DT}
          dataSource={GroupClassList}
          renderTimelineContent={(data, time, flipCard) => {
            const firstSched: any = head(data)
            const color: string = this.getTimelineColor(get('Base', 'TimeType'))

            const spaces = this.getTimelineSpaces(firstSched)

            return (
              <TimelineItem color={color} spaces={spaces}>
                <TimelineCardContent cardCount={data.length}>
                  {data.map((sched: GroupClassListItem, schedIndex) => {
                    const sessionInfo = get(sched, 'SessionInfoList', []) as SessionInfoList[]
                    const atRiskData = getAtRiskData(sessionInfo, sched.LessonsRemaining)
                    const session = new MyBerlitzLesson(sched?.SessionInfoList[0] as any)
                    const isRejected = sched.Status === 'Rejected'
                    const isCancelled = sched.Status === 'Cancelled' || sched.Status === 'Rescheduled'
                    const isPending = sched.Status === 'Pending'
                    const isPastTimeSlot = this.isPastTimeSlot(time, spaces)
                    const isSchedOrientation = isOrientation(sched)
                    const showLessonRegBtn = displayLessonRegBtn(sched, isCutOff)
                    const isCompleted = showLessonRegBtn && sched.LessonRegistered && isPastTimeSlot && !isCancelled
                    const isAbsent =
                      sched.SessionInfoList.length > 0 ? every(sched.SessionInfoList, { Attendance: 'Absent' }) : false

                    const scheduleTime = this.getLessonTime(sched)
                    const startDate =
                      this.checkScheduleCrossover(sched) === 0
                        ? undefined
                        : moment.utc(sched.StartTime).tz(this.props.profile?.Timezone).format(DATE_FORMAT.SIMPLE)

                    return (
                      <TimelineCardBox key={`content-${schedIndex}`} id={`timeline-${sched.Id}`}>
                        {/* {sched && !sched.LearningCenter && (
                          <TimelineBlock
                            color={this.getBlockColor(time, spaces)}
                            time={sched.StartTime}
                            blocked={sched.BlockType === 'Blocked'}
                            travel={sched.BlockType === 'TravelTime'}
                            onBreak={sched.BlockType === 'Break'}
                            note={sched.Note}
                          />
                        )} */}

                        {/* {sched && sched.LearningCenter && ( */}
                        {sched && (
                          <AtRiskContainer isAtRisk={Boolean(atRiskData.length)} colorIconCol={true}>
                            <ListCard
                              icon={
                                <>
                                  <LessonTypeIcon
                                    typeName={
                                      get(sched, 'Type', '') +
                                      (sched?.MaxNumberofStudentsInGroup
                                        ? ` (${sched?.MaxNumberofStudentsInGroup})`
                                        : '')
                                    }
                                    darkText={isCancelled || isRejected}
                                    darkIcon={isPastTimeSlot || isCancelled || isRejected}
                                    customLabel={session.LCSTitle || session.lesson?.UnitFullName} // PS-2683: Instructor should see topic in lesson icon label
                                  />
                                  {sched.LessonRegistered && isPastTimeSlot && !isCancelled && (
                                    <IconStatus>
                                      <CompletedTick color="brand02" />
                                    </IconStatus>
                                  )}
                                  {isCancelled && (
                                    <IconStatus>
                                      <LessonStatus
                                        status={sched.Status}
                                        customLabel={<FormattedMessage id="Cancelled" />}
                                      />
                                    </IconStatus>
                                  )}
                                </>
                              }
                              iconLabel=""
                              iconPosition="top"
                              disableMargin
                              fullHeight
                              color={this.getCardColor(time, spaces, sched.Status, isAbsent)}
                              fullColor={isCancelled || isPending || isRejected || isAbsent}
                              collapsableMargin={false}
                            >
                              <LessonInfoBox>
                                <LessonInfo
                                  Event={firstSched?.Event}
                                  date={
                                    firstSched?.Event
                                      ? moment
                                          .utc(sched.StartTime)
                                          .tz(this.props.profile?.Timezone)
                                          .format(DATE_FORMAT.SIMPLE)
                                      : startDate
                                  }
                                  time={
                                    <PortalTimeFormat
                                      datetime={scheduleTime?.split('-')}
                                      format={[DATE_FORMAT.HM, DATE_FORMAT.HM]}
                                      separator=" - "
                                    />
                                  }
                                  VirtualClassRoomProvider={sched.VirtualClassRoomProvider}
                                  status={sched.Status}
                                  group={<LessonGroupName sched={sched} />}
                                  groupName={sched.GroupName}
                                  atRiskData={atRiskData}
                                  onGroupClick={() => this.handleGroupClick(sched)}
                                  location={sched.LearningCenter}
                                  type={sched.Type}
                                  room={sched.Classroom}
                                  indicators={
                                    ((sched.Comments && sched.Comments.length > 0) || data.length > 1) && (
                                      <Indicators>
                                        {sched.Comments && sched.Comments.length > 0 && <Warning color="error" />}

                                        {data.length > 1 && (
                                          <a onClick={() => flipCard(data)}>
                                            <Flip color="error" />
                                          </a>
                                        )}
                                      </Indicators>
                                    )
                                  }
                                  actionButtons={
                                    firstSched?.Event ? (
                                      <ActionBox>
                                        {firstSched?.Event && (
                                          <EventButtonsWrapper>
                                            <EventOpenMaterialBtn
                                              berlitzEventAttendeeInfo={firstSched?.Event}
                                              fullWidth={false}
                                              onError={(error) => this.setState({ modalEventMaterialError: !!error })}
                                              disable={isPastTimeSlot}
                                            />
                                            <EventJoinOnlineBtnWrapper>
                                              <EventJoinOnlineBtn
                                                berlitzEventAttendeeInfo={firstSched?.Event}
                                                fullWidth={false}
                                              />
                                            </EventJoinOnlineBtnWrapper>
                                            {!isPastTimeSlot &&
                                              firstSched?.Event?.Role !== EVENT_ATTENDEE_ROLE_HOST && (
                                                <a onClick={() => this.toggleOpenCancelModal(true, firstSched?.Event)}>
                                                  <FormattedMessage id="Cancel" defaultMessage="Cancel" />
                                                </a>
                                              )}
                                          </EventButtonsWrapper>
                                        )}
                                      </ActionBox>
                                    ) : (
                                      <ActionBox
                                        className={`${
                                          this.isInMiddleOfLesson(time, spaces) && this.isDateToday() && 'with-report'
                                        } ${sched.Status}`}
                                      >
                                        {/* {this.isInMiddleOfLesson(time, spaces) &&
                                        this.isDateToday() && (
                                          <Button outline small>
                                            <FormattedMessage id="Mid study report" />
                                          </Button>
                                        )} */}

                                        {get(sched, 'MINote') && (
                                          <MIContainer>
                                            <Status cancelled onClick={() => this.setMINote(sched.MINote)}>
                                              <StyledIcon>
                                                <Warning color="brand03" />
                                              </StyledIcon>
                                            </Status>
                                          </MIContainer>
                                        )}

                                        {isCancelled && (
                                          <Status>
                                            <FormattedMessage id="Cancelled" />
                                            <StyledIcon>
                                              <Cancelled color="brand03" />
                                            </StyledIcon>
                                          </Status>
                                        )}

                                        {['confirmed', 'completed'].includes((sched.Status || '').toLowerCase()) &&
                                          isAbsent && (
                                            <Status cancelled>
                                              <FormattedMessage id="Absent" />
                                              <StyledIcon>
                                                <Cancelled color="brand03" />
                                              </StyledIcon>
                                            </Status>
                                          )}

                                        {this.props.isGroup(sched) && (
                                          <CounsellingRegButton
                                            onlyLastLesson
                                            lesson={sched}
                                            url={`/instructors/scheduling/counselling-registration?${this.getQueryParams(
                                              sched,
                                              true
                                            )}`}
                                          />
                                        )}

                                        {sched.SessionInfoList.length < 1 && (
                                          <div>
                                            <TextLabel>
                                              <FormattedMessage id="No students enrolled" />
                                            </TextLabel>
                                          </div>
                                        )}

                                        {showLessonRegBtn && (
                                          <LessonRegButton
                                            type={get(sched, 'Type', '')}
                                            url={`/instructors/scheduling/lesson-registration?${this.getQueryParams(
                                              sched
                                            )}`}
                                          />
                                        )}

                                        <JoinLessonButton
                                          lesson={sched}
                                          startTimeOverride={moment.utc(sched.StartTime, DATE_FORMAT.DT).format()}
                                          endTimeOverride={moment.utc(sched.EndTime, DATE_FORMAT.DT).format()}
                                          studentId={get(sched, 'SessionInfoList.0.CustomerId')}
                                        />
                                        <SelfTechCheck
                                          {...sched}
                                          Wrapper={SelfTechCheckWrapper}
                                          endTimeOverride={moment.utc(sched.EndTime, DATE_FORMAT.DT).format()}
                                        />

                                        {isCompleted && (
                                          <span className="complete-tick">
                                            <CompletedTick color="brand02" />
                                          </span>
                                        )}
                                      </ActionBox>
                                    )
                                  }
                                  pendingButtons={
                                    isPending &&
                                    currentDate.isSameOrAfter(getUserDate(), 'day') &&
                                    withPendingButtons && (
                                      <PendingButtons>
                                        <Button fullWidth small className="actions" onClick={() => onAccept([sched])}>
                                          <FormattedMessage id="Accept" />
                                        </Button>
                                        <Button
                                          fullWidth
                                          ghost
                                          className="ghost actions"
                                          onClick={() => onDecline([sched])}
                                        >
                                          <FormattedMessage id="Decline" />
                                        </Button>
                                      </PendingButtons>
                                    )
                                  }
                                />

                                {!firstSched?.Event &&
                                  !isSchedOrientation &&
                                  !isFlexProgram(sched?.Type) &&
                                  !nonLessons.includes(sched.Type) && (
                                    <LessonInfoFooter
                                      error={isCancelled}
                                      isAtRisk={
                                        (sched.MaterialList.length > 0 ||
                                          sched.LastMaterialList.length > 0 ||
                                          get(sched, 'SessionInfoList.0.StudentNote')) &&
                                        Boolean(atRiskData.length)
                                      }
                                      isBoD1={isBoD1(sched.Type)}
                                    >
                                      <Row>
                                        <Col xs={12} md={7}>
                                          <QueryMaterialData
                                            LastMaterialList={sched.LastMaterialList}
                                            MaterialList={sched.MaterialList}
                                            SessionInfoList={sched.SessionInfoList}
                                            isFlex={isFlexProgram(sched.Type)}
                                            isBoD1={isBoD1(sched.Type)}
                                            ProgramId={sched.ProgramId}
                                            fetchMaterials
                                          >
                                            {({
                                              loading: queryIsLoading,
                                              error,
                                              refetch,
                                              LastMaterialList,
                                              MaterialList,
                                              MaterialOptions,
                                              activeMaterial,
                                            }) => {
                                              if (queryIsLoading) {
                                                return <ViewMaterialLoader />
                                              }

                                              if (error) {
                                                return <ViewMaterialError onRetry={refetch} />
                                              }

                                              return (
                                                <ViewMaterial
                                                  PrevMaterials={LastMaterialList}
                                                  Materials={MaterialList}
                                                  MaterialOptions={MaterialOptions.filter(
                                                    ({ IsTest, Name }) => !IsTest && !/MyBerlitz.*Tutor/.test(Name)
                                                  )}
                                                  isFlex={isFlexProgram(sched.Type)}
                                                  isBoD1={isBoD1(sched.Type)}
                                                  refetch={refetch}
                                                  activeMaterial={activeMaterial}
                                                  SessionInfoList={sched.SessionInfoList}
                                                />
                                              )
                                            }}
                                          </QueryMaterialData>
                                        </Col>

                                        {!isBoD1(sched.Type) && get(sched, 'SessionInfoList.0.StudentNote') && (
                                          <NoteCol xs={12} md={5}>
                                            <label>
                                              <FormattedMessage id="Note" />:
                                            </label>
                                            <p>{get(sched, 'SessionInfoList.0.StudentNote')}</p>
                                          </NoteCol>
                                        )}
                                      </Row>
                                    </LessonInfoFooter>
                                  )}
                              </LessonInfoBox>
                            </ListCard>
                          </AtRiskContainer>
                        )}
                      </TimelineCardBox>
                    )
                  })}
                </TimelineCardContent>
              </TimelineItem>
            )
          }}
        />

        <ModalCancelSeat
          EventAttendeeId={selectedEvent?.EventAttendeeId}
          open={!isEmpty(selectedEvent) && openCancelModal}
          refetchQueries={['GetBerlitzEventAttendee']}
          toggleOpen={(open) =>
            this.toggleOpenCancelModal(open, open ? selectedEvent : ({} as IBerlitzEventAttendeeInfo))
          }
        />

        {!isEmpty(groupModalData) && (
          <GroupProfileModal
            id={groupModalData.groupId}
            open={groupModalOpen}
            onClose={this.toggleGroupModal}
            groupName={groupModalData.groupName}
            programId={groupModalData.programId}
            language={groupModalData.language}
            students={groupModalData.students}
          />
        )}

        {!isEmpty(singleModalData) && (
          <SingleProfileModal open={singleModalOpen} onClose={this.toggleSingleModal} {...singleModalData} />
        )}

        <MINOteModal open={Boolean(miNote)} onClose={() => this.setMINote('')} MINote={miNote} />

        <EventOpenMaterialModal
          open={modalEventMaterialError}
          onClose={() => {
            this.setState({ modalEventMaterialError: false })
            window.location.reload()
          }}
        />
      </Box>
    )
  }
}

export default WithLessonUtilsHOC(WithUserTimeHOC(injectIntl(TimelineSection)))
